<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useHeaderNavContent } from '@/composables/useHeaderNavContent'
import { useDesktopDropdownNav } from '@/composables/useDesktopDropdownNav'
import { useHeaderScroll } from '@/composables/useHeaderScroll'
import { useUIStore } from '@/stores/ui.store'
import { localizedPath } from '@/router/i18nPath'

import DesktopDropdownPanel from './DesktopDropdownPanel.vue'
import Menu from './Menu.vue'
import OffCanvas from './OffCanvas.vue'
import UtilityControl from './UtilityControl.vue'

const { isSticky, isTop, isBottom } = useHeaderScroll(100)

const route = useRoute()
const { t } = useI18n()
const ui = useUIStore()
const { isOffcanvasOpen } = storeToRefs(ui)
const { toggleOffcanvas } = ui

const headerInnerRef = ref<HTMLElement | null>(null)
const isTeleportReady = ref(false)

onMounted(() => {
  isTeleportReady.value = true
})
const {
  activeDesktopNav,
  clearCloseTimer,
  closeDesktopNav,
  closeDesktopNavAndFollowLink,
  focusDesktopNav,
  focusNextDesktopNavElement,
  focusDesktopNavTrigger,
  handleFocusOut,
  openDesktopNav,
  scheduleCloseDesktopNav,
  toggleDesktopNav,
} = useDesktopDropdownNav({
  headerInnerRef,
  routePath: () => route.fullPath,
})

const { desktopNavCards } = useHeaderNavContent()

function handleToggleOffcanvas() {
  closeDesktopNav()
  toggleOffcanvas()
}
</script>

<template>
  <header itemscope itemtype="https://schema.org/WPHeader" class="w-full fixed top-0 z-40">
    <Teleport v-if="isTeleportReady" to="body">
      <Transition
        enter-active-class="transition-opacity duration-350 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="activeDesktopNav"
          aria-hidden="true"
          class="fixed inset-0 z-30 hidden bg-gray-900/30 will-change-[opacity] lg:block"
          @click="closeDesktopNav"
        />
      </Transition>
    </Teleport>

    <div class="headroom" :class="[
      isSticky ? 'headroom--unpinned' : 'headroom--pinned',
      {
        'headroom--top': isTop,
        'headroom--bottom': isBottom,
        'headroom--dropdown-active': !!activeDesktopNav,
      }
    ]">
      <div
        ref="headerInnerRef"
        class="headroom--inner relative overflow-visible"
        @pointerenter="clearCloseTimer()"
        @pointerleave="scheduleCloseDesktopNav()"
        @focusin="clearCloseTimer()"
        @focusout="handleFocusOut"
      >
        <div class="container relative z-20 max-w-screen-2xl mx-auto flex gap-4 lg:gap-6 justify-between items-center">

          <RouterLink
            class="flex self-start flex-row items-center justify-start gap-2 py-4 md:self-center xl:py-5"
            :to="localizedPath('home')"
            aria-label="Go to homepage"
            @mouseover="closeDesktopNav"
            @focus="closeDesktopNav"
            @click="closeDesktopNav"
          >

              <svg class="md:hidden h-auto max-h-6 w-full" id="logo" data-name="Logo"
                xmlns="http://www.w3.org/2000/svg" align="left" viewBox="0 0 23 37" preserveAspectRatio="xMinYMid meet">
                <g class="letters-bold">
                  <path fill="currentColor" class="cls-2"
                    d="m9.91,9.91v4.46h10.91v8.43h-10.91v13.88H0V.99h22.31v8.92h-12.39Z"></path>
                </g>
              </svg>

              <svg class="hidden md:block h-auto max-h-6 w-full" id="logo" data-name="Logo"
                xmlns="http://www.w3.org/2000/svg" align="left" viewBox="0 0 311.81 37.68"
                preserveAspectRatio="xMinYMid meet">
                <g class="letters-bold">
                  <path fill="currentColor" class="cls-2"
                    d="m9.91,9.91v4.46h10.91v8.43h-10.91v13.88H0V.99h22.31v8.92h-12.39Z"></path>
                  <path fill="currentColor" class="cls-2" d="m24.3,36.68V.99h9.91v35.69h-9.91Z"></path>
                  <path fill="currentColor" class="cls-2" d="m53.06,9.91v26.77h-9.91V9.91h-7.44V.99h24.79v8.92h-7.44Z">
                  </path>
                  <path fill="currentColor" class="cls-2"
                    d="m83.32,36.68l-11.3-16.85h-.1v16.85h-9.91V.99h8.92l11.3,16.86h.1V.99h9.92v35.69h-8.92Z"></path>
                  <path fill="currentColor" class="cls-2"
                    d="m118.53,36.68h-22.31V.99h22.31v8.92h-12.39v4.46h10.91v8.43h-10.91v4.96h12.39v8.92Z"></path>
                  <path fill="currentColor" class="cls-2"
                    d="m133.67,37.68c-4.71,0-8.92-.99-12.89-3.97l4.56-7.58,3.12,1.83c2.98,1.74,6.69,1.54,6.69-1.19,0-4.96-13.39-4.46-13.39-15.86,0-6.94,5.45-10.91,11.9-10.91,3.92,0,7.09.55,10.41,3.27l-3.37,7.83c-5.06-4.66-9.52-3.17-9.52-.2,0,3.96,13.88,3.47,13.88,15.86,0,6.94-4.46,10.91-11.4,10.91Z">
                  </path>
                  <path fill="currentColor" class="cls-2"
                    d="m158.96,37.68c-4.71,0-8.92-.99-12.89-3.97l4.56-7.58,3.12,1.83c2.97,1.74,6.69,1.54,6.69-1.19,0-4.96-13.39-4.46-13.39-15.86,0-6.94,5.45-10.91,11.9-10.91,3.92,0,7.09.55,10.41,3.27l-3.37,7.83c-5.06-4.66-9.52-3.17-9.52-.2,0,3.96,13.88,3.47,13.88,15.86,0,6.94-4.46,10.91-11.4,10.91Z">
                  </path>
                </g>
                <g class="letters-thin">
                  <path fill="currentColor" class="cls-1"
                    d="m195.26,36.68l-10.66-15.86h-.99v15.86h-3.72V.99h7.68c5.7,0,10.91,2.98,10.91,9.91s-5.21,9.42-9.67,9.92l11.15,15.86h-4.71Zm-7.68-32.22h-3.97v12.89h3.97c3.72,0,7.19-1.49,7.19-6.45s-3.47-6.44-7.19-6.44Z">
                  </path>
                  <path fill="currentColor" class="cls-1"
                    d="m219.56,37.68c-10.41,0-17.35-7.93-17.35-18.84S209.15,0,219.56,0s17.35,7.93,17.35,18.84-6.94,18.84-17.35,18.84Zm0-34.21c-7.93,0-13.63,6.94-13.63,15.37s5.7,15.37,13.63,15.37,13.63-6.94,13.63-15.37-5.7-15.37-13.63-15.37Z">
                  </path>
                  <path fill="currentColor" class="cls-1"
                    d="m257.26,37.68c-10.41,0-17.35-7.93-17.35-18.84S246.85,0,257.26,0s17.35,7.93,17.35,18.84-6.94,18.84-17.35,18.84Zm0-34.21c-7.93,0-13.63,6.94-13.63,15.37s5.7,15.37,13.63,15.37,13.63-6.94,13.63-15.37-5.7-15.37-13.63-15.37Z">
                  </path>
                  <path fill="currentColor" class="cls-1"
                    d="m308.09,36.68V8.43h-.1l-12.3,23.3-12.29-23.3h-.1v28.26h-3.72V.99h3.72l12.39,23.55,12.39-23.55h3.72v35.69h-3.72Z">
                  </path>
                </g>
              </svg>
            <span class="sr-only">Homepage link</span>
          </RouterLink>

          <Menu
            role="navigation"
            aria-label="Main navigation"
            class="hidden lg:flex flex-1 justify-end items-center gap-4 lg:gap-12"
            :active-desktop-nav="activeDesktopNav"
            @open-desktop-nav="openDesktopNav"
            @toggle-desktop-nav="toggleDesktopNav"
            @focus-desktop-nav="focusDesktopNav"
            @close-desktop-nav="closeDesktopNav"
          />

          <UtilityControl @interact="closeDesktopNav" />

          <nav 
            class="flex gap-4 lg:hidden justify-end items-center"
            :aria-label="t('menu.mobileMenu')"
          >
            <button
              :aria-expanded="isOffcanvasOpen"
              aria-controls="primary-menu"
              :aria-label="isOffcanvasOpen ? t('menu.closeMenu') : t('menu.openMenu')"
              class="hamburger rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              :class="{ 'hamburger--is-open': isOffcanvasOpen }"
              type="button"
              @click="handleToggleOffcanvas"
            >
              <span class="hamburger__item hamburger__item--first"></span>
              <span class="hamburger__item hamburger__item--middle"></span>
              <span class="hamburger__item hamburger__item--last"></span>
            </button>
          </nav>
        </div>

        <DesktopDropdownPanel
          :active-nav="activeDesktopNav"
          :cards-by-nav="desktopNavCards"
          @close="closeDesktopNav"
          @follow-link="closeDesktopNavAndFollowLink"
          @advance-nav="focusNextDesktopNavElement(activeDesktopNav)"
          @return-to-trigger="focusDesktopNavTrigger(activeDesktopNav)"
        />
      </div>
    </div>

    <!-- Offcanvas menu -->
    <OffCanvas role="navigation" :aria-label="t('menu.mobileMenu')" id="primary-menu" />
  </header>
</template>
